import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useWishlistStore, useCartStore } from '@/store';
import { toast } from 'sonner';

const WishlistPage = () => {
  const { items, removeItem } = useWishlistStore();
  const { addItem } = useCartStore();

  const handleAddToCart = (product) => {
    if (!product?.variantId) {
      toast.error('Esse produto não possui variante válida para checkout');
      return;
    }

    addItem(product);
    toast.success('Produto adicionado ao carrinho!');
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-5xl font-bold text-text-primary mb-4">
            Minha Wishlist
          </h1>
          <p className="text-lg text-text-secondary">
            Seus produtos favoritos salvos.
          </p>
        </motion.div>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
            <Heart className="w-10 h-10 mx-auto mb-4 text-accent" />
            <p className="text-lg text-text-secondary mb-6">
              Sua wishlist ainda está vazia.
            </p>
            <Link to="/produtos">
              <Button className="bg-accent text-white hover:bg-accent/90 rounded-full px-8">
                Explorar produtos
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {items.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                  <div className="aspect-[3/4] bg-gray-100 overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>

                <div className="p-6 space-y-3">
                  <p className="text-sm uppercase tracking-widest text-text-muted">
                    {product.category || 'Sem categoria'}
                  </p>

                  <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                    <h2 className="font-body font-semibold text-lg text-text-primary hover:text-accent transition-colors">
                      {product.name}
                    </h2>
                  </Link>

                  <p className="font-heading text-2xl font-bold text-accent">
                    R$ {Number(product.price).toFixed(2)}
                  </p>

                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-accent text-white hover:bg-accent/90 rounded-full"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Carrinho
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => removeItem(product.id)}
                      aria-label={`Remover ${product.name} da wishlist`}
                      className="rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
